import { useTranslation } from 'react-i18next';
import { useCallback } from 'react';
import { color as token } from '@figma/fpl-tokens/tokens.stylex';
import { Trans } from '@/UI/Trans/Trans';
import {
  AppDialogSection,
  AppDialog,
  AppDialogHeader,
  AppDialogTitle,
  AppDialogContent,
  AppDialogActions,
  AppDialogXBtn,
} from '@/UI/AppDialog/AppDialog';
import { TextSmRg } from '@/UI/AppTypography/AppTypography';
import { color, EL_COLORS } from '@/colors';
import { useFplMigrationFlag } from '@/contexts/FplMigrationContext';
import { I18N_KEYS } from '@/language/keys';
import { ButtonContained } from '@/UI/Buttons/AppButton';

/** Design 85255:22374 draws the confirmation at 448. */
const MODAL_WIDTH = 448;

interface PublishToCommunityAccessModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  isUpdating: boolean;
  /**
   * The access the confirm is about to replace. Anything short of public opens this modal, so the
   * copy has to name the state the owner is actually in rather than assume the narrowest one.
   */
  currentAccess: 'invited' | 'workspace';
  /** Set for an org publish, which only widens access to the tool's Weave workspace. */
  orgName?: string;
}

/**
 * Stands between a private workflow or tool and its Community publish: confirming here makes the
 * resource public and goes straight on to the publish modal, which is why the action says Publish
 * rather than Update (design 85255:22374).
 *
 * FPL's header and footer bring their own padding, dividers and close button. The legacy theme has
 * none of that, so it is given the equivalents rather than a second layout.
 */
const PublishToCommunityAccessModal = ({
  open,
  onClose,
  onConfirm,
  isUpdating,
  currentAccess,
  orgName,
}: PublishToCommunityAccessModalProps) => {
  const { t } = useTranslation();
  const isFpl = useFplMigrationFlag();

  const handleClose = useCallback(() => {
    if (!isUpdating) {
      onClose();
    }
  }, [isUpdating, onClose]);

  return (
    <AppDialog
      open={open}
      onClose={handleClose}
      width={MODAL_WIDTH}
      slotProps={{ paper: { sx: { background: token.bg } } }}
      disablePortal
    >
      <AppDialogSection>
        <AppDialogHeader
          component="div"
          sx={{
            px: 2,
            py: 1.5,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderBottom: `1px solid ${EL_COLORS.BoxBorder}`,
          }}
        >
          <AppDialogTitle>
            {t(I18N_KEYS.SHARE_WORKFLOW_MODAL.PUBLISH_TO_FIGMA_COMMUNITY.ACCESS_MODAL.TITLE)}
          </AppDialogTitle>
          {/* Renders nothing under FPL, whose header owns the close button. */}
          <AppDialogXBtn onClick={handleClose} disabled={isUpdating} size={20} />
        </AppDialogHeader>

        {/* `&&` outranks MUI's `DialogTitle + DialogContent` rule, which zeroes the top padding and
          would sit the copy on the header's divider. FPL reads `p` and drops the rest. */}
        <AppDialogContent sx={{ p: 2, '&&': { pt: 2 } }}>
          <TextSmRg color={isFpl ? undefined : color.White64_T} sx={{ display: 'block' }}>
            {orgName ? (
              <Trans
                i18nKey={I18N_KEYS.SHARE_WORKFLOW_MODAL.PUBLISH_TO_FIGMA_COMMUNITY.ORG_DESTINATION.ACCESS_MODAL_BODY}
                values={{ orgName }}
                components={{ bold: <strong /> }}
              />
            ) : (
              <Trans
                i18nKey={
                  currentAccess === 'workspace'
                    ? I18N_KEYS.SHARE_WORKFLOW_MODAL.PUBLISH_TO_FIGMA_COMMUNITY.ACCESS_MODAL.BODY_WORKSPACE
                    : I18N_KEYS.SHARE_WORKFLOW_MODAL.PUBLISH_TO_FIGMA_COMMUNITY.ACCESS_MODAL.BODY
                }
                components={{ bold: <strong /> }}
              />
            )}
          </TextSmRg>
        </AppDialogContent>

        <AppDialogActions sx={{ px: 2, py: 1.5, gap: 1 }}>
          <ButtonContained mode="filled-secondary" size="small" onClick={handleClose} disabled={isUpdating}>
            {t(I18N_KEYS.GENERAL.CANCEL)}
          </ButtonContained>
          <ButtonContained mode="filled-light" size="small" onClick={onConfirm} disabled={isUpdating}>
            {t(I18N_KEYS.SHARE_WORKFLOW_MODAL.PUBLISH_TO_FIGMA_COMMUNITY.ACCESS_MODAL.CONFIRM)}
          </ButtonContained>
        </AppDialogActions>
      </AppDialogSection>
    </AppDialog>
  );
};

export default PublishToCommunityAccessModal;
